import { Link } from "react-router-dom";

import { Spinner } from "@/components/ui";
import { LAST_UPDATED, SUPPORT_EMAIL } from "@/constants/legal";
import { ROUTES } from "@/constants/routes";
import { useCompanyLegalInfo } from "@/hooks";

import {
  BulletList,
  ContactBlock,
  DataTable,
  DefinitionList,
  NumberedList,
  Section,
  Subsection,
} from "./components";

export const PrivacyPolicyContent = () => {
  const { company, isLoading } = useCompanyLegalInfo();

  if (isLoading) return <Spinner className="mx-auto mt-32 size-8" />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-16">
      <header className="mb-10 border-border border-b pb-8">
        <p className="font-semibold text-foreground text-sm uppercase tracking-wider">
          Dualio.ai
        </p>
        <h1 className="mt-2 font-bold text-3xl text-foreground tracking-tight sm:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-3 text-muted-foreground text-sm">Last Updated: {LAST_UPDATED}</p>
      </header>

      <div className="space-y-6 text-[15px] text-foreground leading-relaxed">
        <p>
          This Privacy Policy ("Policy") describes how {company.companyName} (
          {company.registrationNumber}), with a registered office at {company.address}{" "}
          ("we", "us", "our", or "Company"), collects, processes, uses, shares, and
          protects your personal data when you use the Dualio.ai platform and associated
          services (the "Service").
        </p>
        <p>
          By using the Service, you confirm that you have read, understood, and agree to
          this Privacy Policy. If you do not agree, do not use the Service.
        </p>
        <p>
          This Policy should be read together with our{" "}
          <Link
            to={ROUTES.TERMS}
            className="font-medium text-foreground hover:text-muted-foreground"
          >
            Terms of Service
          </Link>
          . Capitalised terms not defined here have the meaning given in the Terms of
          Service.
        </p>

        <div className="rounded-2xl border border-primary bg-card p-5">
          <h2 className="font-bold text-foreground text-sm uppercase tracking-wider">
            Key Points — What You Need to Know
          </h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground text-sm">
            <li>
              We collect: email address, device data, analytics data, uploaded
              photographs, and biometric data (facial geometry) derived from your photos
              to create AI avatars.
            </li>
            <li>
              We do not sell your biometric data. We do not share your photos with third
              parties except service providers acting under our instructions.
            </li>
            <li>
              Biometric data is deleted within 12 months of collection or upon account
              deletion, whichever is earlier.
            </li>
            <li>
              To exercise your data rights or delete your data, contact{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium underline hover:no-underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </li>
          </ul>
        </div>
      </div>

      <Section title="Contents">
        <ol className="list-decimal space-y-1 pl-6 text-[15px] text-foreground">
          <li>Personal Data We Collect</li>
          <li>Biometric Data and Photo Processing</li>
          <li>Purposes and Legal Bases for Processing</li>
          <li>How We Share Your Personal Data</li>
          <li>Your Privacy Rights</li>
          <li>US State Privacy Rights</li>
          <li>Age Limitations</li>
          <li>International Data Transfers</li>
          <li>Data Security</li>
          <li>Data Retention</li>
          <li>Cookies and Tracking Technologies</li>
          <li>AI-Generated Content and Data</li>
          <li>Third-Party Links and Services</li>
          <li>Changes to This Privacy Policy</li>
          <li>Data Controller</li>
          <li>Contact Us</li>
        </ol>
      </Section>

      <Section title="1. Personal Data We Collect">
        <p>
          We collect personal data in three ways: directly from you, automatically through
          your use of the Service, and from third-party sources.
        </p>

        <Subsection title="1.1. Data You Provide Directly">
          <DefinitionList
            items={[
              {
                label: "a. Account Identifiers",
                body: "Your email address and password, collected when you register for the Service.",
              },
              {
                label: "b. Photographs and Biometric Data",
                body: "When you upload a photograph to create an AI avatar, we collect your photograph and derive biometric identifiers from it, including facial geometry data. See Section 2 for full details.",
              },
              {
                label: "c. Onboarding and Profile Data",
                body: "Information you provide during onboarding, such as content creation goals, niche preferences, or business type.",
              },
              {
                label: "d. Payment Information",
                body: "Payment details (card number, billing address) are collected directly by our payment processor. We do not store full card details. We retain transaction identifiers and subscription status.",
              },
              {
                label: "e. Communications",
                body: "Any messages, feedback, or support requests you send to us, including through email or in-app messaging.",
              },
            ]}
          />
        </Subsection>

        <Subsection title="1.2. Data Collected Automatically">
          <DefinitionList
            items={[
              {
                label: "a. Online Activity Data",
                body: "We log how you interact with the Service: features accessed, content generated, time spent, actions taken, frequency of use, and navigation patterns.",
              },
              {
                label: "b. Device Data",
                body: "Browser type and version, operating system, IP address, time zone, language settings, device type and model, Internet service provider.",
              },
              {
                label: "c. Cookies and Tracking Technologies",
                body: "We use cookies, SDKs, pixels, and similar technologies. See Section 11 for full details.",
              },
              {
                label: "d. Advertising IDs",
                body: "Where applicable, browser-based advertising identifiers or other tracking identifiers used for attribution and ad measurement.",
              },
            ]}
          />
        </Subsection>

        <Subsection title="1.3. Data from Third-Party Sources">
          <BulletList
            items={[
              "Analytics providers (Amplitude): aggregated behavioural analytics",
              "Attribution partners (AppsFlyer): campaign attribution data linking your visit to an ad source",
              "Payment processors (Solidgate): transaction status and fraud signals",
              "Advertising platforms (Meta Ads, Google Ads): ad impression and conversion data",
            ]}
          />
        </Subsection>
      </Section>

      <Section title="2. Biometric Data and Photo Processing">
        <NumberedList
          prefix="2"
          items={[
            "IMPORTANT: This section describes our processing of biometric data. Please read it carefully before uploading a photograph.",
            'When you upload a photograph to create an AI avatar, the Service processes your image using computer vision and machine learning technologies to extract facial geometry data ("Biometric Data"). This Biometric Data is used solely to generate your personalised AI avatar.',
            "Categories of biometric data collected: facial geometry, facial landmark coordinates, and structural facial data necessary for avatar rendering. We do not collect fingerprints, voiceprints, iris scans, or DNA.",
            "Legal basis for processing: your express, informed consent, provided at the time of photograph upload and confirmed by your acceptance of this Privacy Policy and the Terms of Service.",
            "Purposes: avatar generation and rendering; quality assurance; compliance with legal obligations (including minor detection and prohibited content screening).",
            "We will not: sell your Biometric Data; use it for purposes other than those listed in 2.5; share it with third parties except service providers acting under binding data processing agreements and solely in service of these purposes.",
            "Retention: Biometric Data is retained for no longer than 12 months from the date of collection, or until deletion of your Account, whichever is earlier. After this period, Biometric Data is permanently and irreversibly destroyed.",
            `Illinois (BIPA): We comply with the Illinois Biometric Information Privacy Act. We have a written retention schedule (this Policy) and will not profit from your Biometric Data. You may request deletion of your Biometric Data at any time by contacting ${SUPPORT_EMAIL}.`,
            `Texas and Washington: Similar rights under CUBI (Texas) and the Washington My Health MY Data Act (where applicable) may apply to your Biometric Data. Contact ${SUPPORT_EMAIL} to exercise these rights.`,
            "Content screening: All uploaded photographs are screened using automated tools (including AWS Rekognition) to detect and prevent: (a) photographs of minors; (b) photographs of third parties submitted without consent; (c) content that violates our Terms. Photographs that fail moderation checks are rejected and not processed further.",
            `You may withdraw consent to biometric processing at any time. Withdrawal will prevent you from using avatar-creation features. To withdraw, submit a deletion request to ${SUPPORT_EMAIL}.`,
          ]}
        />
      </Section>

      <Section title="3. Purposes and Legal Bases for Processing">
        <p>
          We process your personal data for the following purposes and on the following
          legal bases:
        </p>
        <DataTable
          headers={["Purpose", "Description", "Data Categories", "Legal Basis"]}
          rows={[
            [
              "Service provision and account management",
              "Creating and managing your account; enabling video generation; processing payments; preventing technical errors",
              "All categories",
              "Contract performance",
            ],
            [
              "Biometric avatar creation",
              "Processing your photograph and biometric data to generate your personalised AI avatar",
              "Photographs; Biometric Data",
              "Consent",
            ],
            [
              "Service communications",
              "Sending account updates, security notices, Terms/Policy changes, transactional emails",
              "Identifiers; Account data",
              "Contract performance / Legitimate interest",
            ],
            [
              "Service analytics and improvement",
              "Understanding usage patterns; improving features; product development and testing",
              "Online activity; Device data; Analytics data",
              "Legitimate interest",
            ],
            [
              "Marketing communications",
              "Sending promotional emails about the Service (where legally permitted)",
              "Identifiers",
              "Consent (or legitimate interest in permitted jurisdictions)",
            ],
            [
              "Ad personalisation and attribution",
              "Delivering targeted advertising; measuring campaign effectiveness",
              "Device data; Advertising IDs; Cookies",
              "Consent / Legitimate interest",
            ],
            [
              "Legal compliance",
              "Complying with legal obligations, regulatory requests, court orders",
              "All categories",
              "Legal obligation",
            ],
            [
              "Fraud prevention and security",
              "Detecting and preventing fraud, abuse, and unauthorised access",
              "All categories",
              "Legitimate interest",
            ],
            [
              "Defence of legal claims",
              "Retaining data to defend or bring legal claims",
              "All categories",
              "Legitimate interest / Legal obligation",
            ],
            [
              "Content moderation",
              "Screening content for prohibited material (CSAM, impersonation, etc.)",
              "Photographs; Biometric Data; Content data",
              "Legal obligation / Legitimate interest",
            ],
          ]}
        />
        <p>
          Legitimate interests relied upon: improving the Service; promoting the Service
          proportionately; preventing fraud; defending legal claims; maintaining platform
          safety.
        </p>
      </Section>

      <Section title="4. How We Share Your Personal Data">
        <Subsection title="4.1. Service Providers">
          <p>
            We share personal data with third-party service providers who process data on
            our behalf under binding contractual data processing agreements. These
            providers may only use your data for the purposes we specify.
          </p>
          <DataTable
            headers={["Category", "Examples / Providers", "Purpose", "Data Shared"]}
            rows={[
              [
                "Cloud storage and infrastructure",
                "Amazon Web Services (AWS)",
                "Hosting, storage, backend services",
                "All categories",
              ],
              [
                "Video rendering pipeline",
                "Proprietary AI pipeline (Kling, InfiniteTalk)",
                "AI avatar and video generation",
                "Photographs; Biometric Data; Content inputs",
              ],
              [
                "Content moderation",
                "AWS Rekognition, PhotoDNA",
                "Minor detection; celebrity detection; prohibited content screening",
                "Photographs",
              ],
              [
                "Performance monitoring",
                "Firebase Crashlytics, Firebase Performance",
                "Error tracking, infrastructure monitoring",
                "Device data; Online activity",
              ],
              [
                "Product analytics",
                "Amplitude",
                "User behaviour analytics, funnel analysis",
                "Online activity; Device data",
              ],
              [
                "Attribution analytics",
                "AppsFlyer, Facebook SDK",
                "Ad campaign attribution",
                "Device data; Advertising IDs",
              ],
              [
                "Marketing and advertising",
                "Meta Ads, Google Ads",
                "Ad delivery and measurement",
                "Identifiers; Device data; Advertising IDs",
              ],
              [
                "Payment processing",
                "Solidgate",
                "Subscription billing and payment processing",
                "Payment data; Identifiers",
              ],
              [
                "Email communications",
                "Third-party email provider",
                "Transactional and support emails",
                "Identifiers",
              ],
            ]}
          />
        </Subsection>

        <Subsection title="4.2. Law Enforcement and Public Authorities">
          <p>
            We may disclose personal data to law enforcement agencies, courts, regulators,
            or other public authorities: (a) where required by applicable law; (b) to
            protect the rights, safety, or property of the Company, our users, or others;
            (c) to detect, investigate, or prevent fraud or security breaches; (d) to
            comply with legal process.
          </p>
        </Subsection>

        <Subsection title="4.3. Business Transfers">
          <p>
            In the event of a merger, acquisition, sale, or restructuring of all or part
            of the Company's business, personal data may be transferred as part of that
            transaction. We will notify you before your data is transferred and becomes
            subject to a different privacy policy.
          </p>
        </Subsection>

        <Subsection title="4.4. Affiliates">
          <p>
            We may share personal data with affiliated entities within the Imponilox
            group, who will process it in accordance with this Policy.
          </p>
        </Subsection>

        <Subsection title="4.5. No Sale of Biometric Data">
          <p>
            We do not and will not sell, lease, or trade your biometric data to any third
            party for any purpose.
          </p>
        </Subsection>

        <Subsection title="4.6. CCPA Categories of Third Parties">
          <p>
            For California residents: the categories of third parties to whom personal
            information was disclosed that may constitute a "sale" or "sharing" under
            CCPA/CPRA are: marketing service providers and data analytics providers. You
            have the right to opt out. See Section 6.
          </p>
        </Subsection>
      </Section>

      <Section title="5. Your Privacy Rights">
        <p>
          Subject to applicable law, you have the following rights regarding your personal
          data:
        </p>
        <DefinitionList
          items={[
            {
              label: "Right of Access",
              body: "You may request a copy of the personal data we hold about you.",
            },
            {
              label: "Right to Rectification",
              body: "You may request correction of inaccurate or incomplete personal data.",
            },
            {
              label: "Right to Erasure",
              body: "You may request deletion of your personal data. We will comply unless we are required by law to retain it.",
            },
            {
              label: "Right to Restriction",
              body: "You may request that we restrict processing of your data in certain circumstances.",
            },
            {
              label: "Right to Portability",
              body: "You may request a copy of your data in a machine-readable format (JSON or equivalent).",
            },
            {
              label: "Right to Object",
              body: "You may object to processing based on legitimate interests, including direct marketing.",
            },
            {
              label: "Right to Withdraw Consent",
              body: "Where processing is based on consent (including biometric data processing), you may withdraw consent at any time without affecting the lawfulness of prior processing.",
            },
            {
              label: "Right to Complain",
              body: "You have the right to lodge a complaint with a competent data protection supervisory authority. For EEA residents, this is the data protection authority in your country of residence. For Cyprus, the Commissioner for Personal Data Protection (www.dataprotection.gov.cy).",
            },
          ]}
        />
        <p>
          To exercise any of these rights, contact us at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-foreground hover:text-muted-foreground"
          >
            {SUPPORT_EMAIL}
          </a>
          . We will respond within the timeframe required by applicable law (generally 30
          days). We may need to verify your identity before processing your request.
        </p>
        <p>
          You may authorise an agent to submit requests on your behalf. We may require
          proof of authorisation before acting on such requests.
        </p>
      </Section>

      <Section title="6. US State Privacy Rights">
        <Subsection title="6.1. California (CCPA / CPRA)">
          <p>California residents have the following additional rights:</p>
          <BulletList
            items={[
              "Right to know: categories of personal information collected, sources, business purposes, and third parties to whom it was disclosed or sold",
              "Right to delete: request deletion of personal information, subject to exceptions",
              "Right to correct: request correction of inaccurate personal information",
              "Right to opt out of sale/sharing: opt out of the sale or sharing of personal information for cross-contextual behavioural advertising",
              "Right to limit use of sensitive personal information: limit use of sensitive data (including biometric data) to necessary purposes",
              "Right of no retaliation: you will not be discriminated against for exercising these rights",
            ]}
          />
          <p>
            To exercise California rights: email {SUPPORT_EMAIL} with the subject line
            "CCPA Privacy Request". We will respond within 45 days (extendable by an
            additional 45 days with notice).
          </p>
          <p>
            California "Shine the Light" (Civil Code § 1798.83): California residents may
            request once per year information about personal data disclosed to third
            parties for their direct marketing purposes. Email {SUPPORT_EMAIL} with
            subject "California Shine the Light Request".
          </p>
          <p>
            Do Not Sell My Personal Information: To opt out of the sale or sharing of your
            personal information, contact {SUPPORT_EMAIL} or use the opt-out mechanism
            available on the Service.
          </p>
        </Subsection>

        <Subsection title="6.2. Other US State Residents">
          <p>
            Residents of Colorado, Connecticut, Delaware, Indiana, Iowa, Kentucky,
            Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
            Rhode Island, Tennessee, Texas, Utah, and Virginia may have additional privacy
            rights under applicable state law, including rights to access, correct,
            delete, and opt out of targeted advertising and data sales.
          </p>
          <p>
            To exercise these rights, contact {SUPPORT_EMAIL}. If your request is denied,
            you have the right to appeal. To appeal, email us clearly indicating "USA
            PRIVACY APPEAL REQUEST."
          </p>
        </Subsection>

        <Subsection title="6.3. Biometric Privacy — Illinois (BIPA), Texas (CUBI), Washington">
          <p>
            As described in Section 2, residents of Illinois, Texas, and Washington have
            specific statutory rights regarding biometric data. We comply with all
            applicable state biometric privacy laws. Contact {SUPPORT_EMAIL} to exercise
            your biometric privacy rights.
          </p>
        </Subsection>
      </Section>

      <Section title="7. Age Limitations">
        <p>
          The Service is not directed to persons under 18 years of age. We do not
          knowingly collect personal data from minors. If you are a parent or guardian and
          believe your child has submitted personal data to us, please contact us
          immediately at {SUPPORT_EMAIL}. Upon confirmation, we will delete such data
          promptly.
        </p>
        <p>
          We use automated screening tools to detect and reject photographs of minors
          submitted for avatar creation. Any account that we reasonably determine is used
          by or on behalf of a minor will be immediately suspended.
        </p>
      </Section>

      <Section title="8. International Data Transfers">
        <p>
          We are incorporated in Cyprus and may transfer personal data to countries
          outside the European Economic Area (EEA) for the purposes described in this
          Policy, including to the United States for infrastructure and analytics
          purposes.
        </p>
        <p>
          For transfers from the EEA to third countries, we implement appropriate
          safeguards including:
        </p>
        <BulletList
          items={[
            "Standard Contractual Clauses (SCCs) approved by the European Commission",
            "Transfers to countries with adequacy decisions from the European Commission",
            "Other appropriate transfer mechanisms as required by applicable law",
          ]}
        />
        <p>
          You may request a copy of the safeguards we have put in place for international
          transfers by contacting {SUPPORT_EMAIL}.
        </p>
      </Section>

      <Section title="9. Data Security">
        <p>
          We implement technical and organisational security measures to protect your
          personal data against accidental or unlawful destruction, loss, alteration,
          unauthorised disclosure, or access. These measures include encryption of data in
          transit and at rest, access controls, regular security assessments, and vendor
          security reviews.
        </p>
        <p>
          Despite these measures, no method of transmission over the internet or
          electronic storage is completely secure. We cannot guarantee absolute security.
          In the event of a data breach that is likely to result in a high risk to your
          rights and freedoms, we will notify you as required by applicable law.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your account
          credentials. Notify us immediately at {SUPPORT_EMAIL} if you suspect any
          unauthorised access to your account.
        </p>
      </Section>

      <Section title="10. Data Retention">
        <p>
          We retain personal data for as long as necessary to fulfil the purposes
          described in this Policy, including:
        </p>
        <DataTable
          headers={["Data Category", "Retention Period", "Reason"]}
          rows={[
            [
              "Account data (email, profile)",
              "Duration of account + 30 days after deletion",
              "Service provision; fraud prevention",
            ],
            [
              "Photographs uploaded for avatar creation",
              "Duration of avatar use, maximum 12 months",
              "Avatar generation; quality assurance",
            ],
            [
              "Biometric Data (facial geometry)",
              "Maximum 12 months from collection OR until account deletion, whichever is earlier",
              "BIPA/CUBI compliance; avatar generation",
            ],
            [
              "Generated AI video content",
              "90 days from generation (unless downloaded)",
              "Service provision",
            ],
            [
              "Transaction and payment records",
              "7 years from transaction date",
              "Legal and accounting obligations",
            ],
            [
              "Analytics and usage data",
              "24 months rolling",
              "Product improvement; legitimate interest",
            ],
            [
              "Support communications",
              "3 years from last interaction",
              "Legal claim defence",
            ],
            [
              "Legal hold data",
              "As required by applicable law or court order",
              "Legal obligation",
            ],
          ]}
        />
        <p>
          Upon account deletion, we will delete or anonymise your personal data within 30
          days, except where longer retention is required by law.
        </p>
      </Section>

      <Section title="11. Cookies and Tracking Technologies">
        <p>
          We use cookies and similar technologies (pixels, SDKs, local storage) on the
          Service. The following categories of technologies are used:
        </p>
        <DefinitionList
          items={[
            {
              label: "Strictly Necessary",
              body: "Required for the Service to function. Cannot be disabled. These enable authentication, security features, and core functionality.",
            },
            {
              label: "Functional",
              body: "Remember your preferences and personalise your experience.",
            },
            {
              label: "Performance / Analytics",
              body: "Collect aggregated data about how users interact with the Service, which features are most used, and where improvements are needed.",
            },
            {
              label: "Targeting / Advertising",
              body: "Used with our advertising partners to deliver relevant ads and measure campaign effectiveness. These may result in ads being shown to you on third-party platforms.",
            },
          ]}
        />
        <p>
          You can control non-essential cookies through your browser settings or the
          cookie preference tools available on the Service. Disabling certain cookies may
          affect the functionality of the Service.
        </p>
        <p>
          For targeted advertising opt-out, see Section 6 (for US state rights) or contact
          {SUPPORT_EMAIL}.
        </p>
      </Section>

      <Section title="12. AI-Generated Content and Data">
        <NumberedList
          prefix="12"
          items={[
            "Content you generate using the Service (AI videos, avatars) may be stored on our servers for the retention period described in Section 10. This content is associated with your account.",
            "Text inputs, product URLs, or other prompts you submit to the Service are processed to generate AI Content and may be retained for quality assurance, safety review, and model improvement purposes, subject to our retention schedule.",
            "We embed C2PA (Coalition for Content Provenance and Authenticity) metadata in all AI-generated video outputs. This metadata identifies the content as AI-generated and includes provenance information. This metadata travels with the file when downloaded and distributed.",
            "We do not use your personal photographs or biometric data to train AI models belonging to the Company or any third party, except to the extent expressly required to generate your avatar as part of the Service.",
          ]}
        />
      </Section>

      <Section title="13. Third-Party Links and Services">
        <p>
          The Service may contain links to third-party websites, platforms, or services.
          This Policy does not apply to those third parties. We encourage you to review
          the privacy policies of any third-party services you use. We are not responsible
          for the privacy practices or content of third parties.
        </p>
      </Section>

      <Section title="14. Changes to This Privacy Policy">
        <p>
          We may update this Policy from time to time. If we make material changes, we
          will notify you by email (to the address associated with your account) or by
          prominent notice within the Service at least 14 days before the changes take
          effect. The "Last Updated" date at the top of this Policy reflects the most
          recent revision.
        </p>
        <p>
          Your continued use of the Service after the effective date of any changes
          constitutes your acceptance of the revised Policy.
        </p>
      </Section>

      <Section title="15. Data Controller">
        <p>The data controller for personal data processed through the Service is:</p>
        <ContactBlock company={company} />
        <p>
          For data protection matters, you may contact us directly at {SUPPORT_EMAIL}.
        </p>
      </Section>

      <Section title="16. Contact Us">
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy
          or the processing of your personal data, please contact us:
        </p>
        <ContactBlock company={company} />
        <p>
          We will respond to your inquiry within 30 days (or within the timeframe required
          by applicable law).
        </p>
        <p>
          EEA residents: You also have the right to lodge a complaint with the Cyprus data
          protection supervisory authority: Commissioner for Personal Data Protection, 1
          Iasonos Street, 1082 Nicosia, Cyprus (www.dataprotection.gov.cy), or the
          supervisory authority in your country of residence.
        </p>
      </Section>

      <footer className="mt-12 border-border border-t pt-6 text-muted-foreground text-sm">
        <p>
          {company.companyName} ({company.registrationNumber}) with a registered office at{" "}
          {company.address}.
        </p>
        <p className="mt-2">Last Updated: {LAST_UPDATED}</p>
      </footer>
    </div>
  );
};
